/**
 * ShopifyChannelStatus — the Deploy → Channels body for the Shopify tile
 * (deliberately SLIM by decision 2026-07-16: status + widget toggle hand-off; the
 * full store card lives in AI Studio → Apps).
 *
 *   connected     → status card: store, the honest Live / Not live chip (from the
 *                   published theme's app-embed state) and the theme-editor
 *                   hand-off (new tab; the embed arrives pre-activated).
 *   not connected → the App-Store install prompt (2026-08-17), with the classic
 *                   manual-embed guide demoted behind a disclosure. The app is the
 *                   better path by a wide margin (catalog mirror, cart, store
 *                   knowledge) and the snippet is the fallback, not the default.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faSpinner, faArrowUpRightFromSquare, faCircleInfo, faCheck,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import ChannelGuide from './ChannelGuide.js';
import './ShopifyChannelStatus.css';

const API = process.env.REACT_APP_API_BASE_URL;

// The public listing ("Ultimo: AI Agent & Live Chat"). Linking out is all the
// portal can do here: binding a store is dual-auth inside the Shopify admin
// iframe (backend doctrine, src/controller/shopify/portal.py), so there is no
// portal-side connect to offer — and with no binding there is no shop domain to
// deep-link into either.
const APP_STORE_URL = 'https://apps.shopify.com/ultimo-bots';

export default function ShopifyChannelStatus({ guide, botId }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);   // null loading · {connected: false} · full status
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}apps/shopify/status?bot_id=${encodeURIComponent(botId)}`);
        const j = await r.json().catch(() => null);
        if (alive) setStatus(r.ok && j ? j : { connected: false });
      } catch {
        if (alive) setStatus({ connected: false });
      }
    })();
    return () => { alive = false; };
  }, [botId]);

  if (!botId || (status && !status.connected)) {
    return <ShopifyInstallPrompt guide={guide} botId={botId} />;
  }
  if (!status) {
    return <div className="shch-loading"><FontAwesomeIcon icon={faSpinner} spin /></div>;
  }

  const live = status.widget_live;   // true | false | null (cannot tell)
  const openEditor = () => window.open(status.links && status.links.theme_editor_url, '_blank', 'noopener,noreferrer');

  return (
    <div className="shch">
      <div className="shch-connected">
        <FontAwesomeIcon icon={faCircleCheck} className="shch-check" />
        <div className="shch-connected-text">
          <span className="shch-connected-title">
            {t('channels_shopify_connected_title', 'Connected to {{store}}', { store: status.store_name || status.shop_domain })}
          </span>
          <span className="shch-connected-sub">
            {t('channels_shopify_connected_sub', 'Your agent is installed as a native Shopify app. Products and store policies sync automatically.')}
          </span>
        </div>
      </div>

      <div className="shch-widget-row">
        <div className="shch-widget-text">
          <span className="shch-widget-label">{t('channels_shopify_widget_label', 'Chat on your storefront')}</span>
          <span className="shch-widget-desc">
            {live === true
              ? t('channels_shopify_widget_live_desc', 'Shoppers can chat with your agent.')
              : live === false
                ? t('channels_shopify_widget_off_desc', 'Not on yet. One click, then hit Save in the theme editor.')
                : t('channels_shopify_widget_unknown_desc', 'Manage the chat in your theme editor.')}
          </span>
        </div>
        <button type="button" className="shch-widget-btn" onClick={openEditor}>
          {live === true && <span className="shch-dot live" aria-hidden="true" />}
          {live === false && <span className="shch-dot off" aria-hidden="true" />}
          {live === true
            ? t('channels_shopify_widget_live', 'Live')
            : live === false
              ? t('channels_shopify_widget_off', 'Not live')
              : t('manage', 'Manage')}
          {' '}<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </button>
      </div>
    </div>
  );
}

/**
 * The not-connected body: install the app (primary), or paste the snippet
 * (fallback, collapsed). The account note is load-bearing — the app's onboarding
 * pre-fills the account step from the SHOP's contact email, so a merchant who is
 * already signed in here can end up with a second account and a store bound to
 * the wrong workspace.
 */
function ShopifyInstallPrompt({ guide, botId }) {
  const { t } = useTranslation();
  const [manualOpen, setManualOpen] = useState(false);

  // Claims cross-checked against features/29-app-shopify.md (LIVE listing) — do
  // not add order tracking or anything customer-data shaped, which the app
  // deliberately does not do.
  const perks = [
    t('channels_shopify_perk_catalog', 'Products, variants, prices and stock stay mirrored as your store changes.'),
    t('channels_shopify_perk_cart', 'Shoppers find products in chat and add them to your real storefront cart.'),
    t('channels_shopify_perk_knowledge', 'Policies, pages and blog posts import as knowledge on install.'),
    t('channels_shopify_perk_nocode', 'Nothing to paste, and nothing to redo when you switch theme.'),
  ];

  return (
    <div className="shch-install">
      <div className="shch-promo">
        <img className="shch-promo-logo" src="/shopify-logo.svg" alt="" />
        <div className="shch-promo-text">
          <span className="shch-promo-title">
            {t('channels_shopify_promo_title', 'Install the Ultimo Bots app for Shopify')}
          </span>
          <span className="shch-promo-sub">
            {t('channels_shopify_promo_sub', 'It connects this agent to your store, imports your catalog and policies, and puts the chat on every page without touching theme code. Free for 7 days.')}
          </span>
        </div>
        <a
          className="shch-promo-btn"
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('channels_shopify_promo_cta', 'View on the Shopify App Store')}
          {' '}<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      </div>

      <ul className="shch-perks">
        {perks.map((perk) => (
          <li key={perk}>
            <FontAwesomeIcon icon={faCheck} />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      <div className="shch-hint">
        <FontAwesomeIcon icon={faCircleInfo} />
        <span>
          {t('channels_shopify_account_note', 'During setup, sign in with this Ultimo Bots account and choose to connect an existing agent. Shopify pre-fills the account step from your store contact email, so a different address there creates a second account and binds the store to the wrong workspace.')}
        </span>
      </div>

      <p className="shch-already">
        {t('channels_shopify_already_installed', 'Already installed? Open the app from Apps in your Shopify admin, then connect this agent there.')}
      </p>

      <div className="shch-manual">
        <button
          type="button"
          className="shch-manual-toggle"
          onClick={() => setManualOpen((open) => !open)}
          aria-expanded={manualOpen}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`shch-manual-caret${manualOpen ? ' open' : ''}`}
          />
          {t('channels_shopify_manual_toggle', 'Prefer to paste the code snippet yourself?')}
        </button>
        {manualOpen && (
          <div className="shch-manual-body">
            <p className="shch-manual-note">
              {t('channels_shopify_manual_note', 'The snippet adds the chat only. Your agent gets no product data from Shopify, and you need to paste it again whenever you switch or duplicate your theme.')}
            </p>
            <ChannelGuide guide={guide} botId={botId} />
          </div>
        )}
      </div>
    </div>
  );
}
